import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import { useTableData } from "../../hooks/useTableData";
import { creditDebitReportsConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import { useRole } from "hooks/useAuth";

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — credit_debit product
// ─────────────────────────────────────────────────────────────────────────────
const CREDIT_DEBIT_COLUMNS = {
  Retailer: [
    "id", "ref_no", "ack_no", "transection_type", "product",
    "receiver_phone", "sender_phone", "amount", "sender_name",
    "receiver_name", "remark", "created_at", "status",
    "Opening Balance BC", "BC Commission", "BC TDS", "BC Charge",
    "BC GST", "Closing Balance BC",
  ],
  "API Partner": [
    "id", "ref_no", "ack_no", "transection_type", "product",
    "receiver_phone", "sender_phone", "amount", "sender_name",
    "receiver_name", "remark", "created_at", "status",
    "Opening Balance BC", "BC Commission", "BC TDS", "BC Charge",
    "BC GST", "Closing Balance BC",
  ],
  Distributer: [
    "id", "ref_no", "ack_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance Dist", "Commission Dist", "TDS Dist",
    "Charge Dist", "GST Dist", "Closing Balance Dist",
    "bank_rrn", "card_number",
  ],
  "Master Distributor": [
    "id", "ref_no", "ack_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance SuperDist", "Commission SuperDist", "TDS SuperDist",
    "Charge SuperDist", "GST SuperDist", "Closing Balance SuperDist",
    "bank_rrn", "card_number",
  ],
  "White label": [
    "id", "ref_no", "ack_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance WL", "Commission WL", "TDS WL",
    "Charge WL", "GST WL", "Closing Balance WL",
    "bank_rrn", "card_number",
  ],
  Admin: [
    "id", "ID", "BC Name", "Product Type", "Product", "Amount",
    "Opening Balance Admin", "Commission", "TDS", "Charge", "GST",
    "Closing Balance Admin", "Opening Balance BC", "BC Commission",
    "BC TDS", "BC Charge", "BC GST", "Closing Balance BC",
    "Customer No", "Status", "Wallet Txn", "TXN Flow",
    "REF No", "ACK NO", "bank_rrn", "card_number", "created_at",
  ],
  "Main Admin": null, // null = show all
};

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — fund_transfer product (wallet_transfer / wallet_return)
// These rows have no commission/tds/charge/gst — only opening/closing balance
// ─────────────────────────────────────────────────────────────────────────────
const FUND_TRANSFER_COLUMNS = {
  Retailer: [
    "id", "ref_no", "transection_type", "product",
    "sender_phone", "receiver_phone", "amount",
    "created_at", "status", "remark",
    "Opening Balance BC", "Closing Balance BC",
  ],
  Distributer: [
    "id", "ref_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance Dist", "Closing Balance Dist",
  ],
  "Master Distributor": [
    "id", "ref_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance SuperDist", "Closing Balance SuperDist",
  ],
  "White label": [
    "id", "ref_no", "transection_type", "product",
    "number", "amount", "created_at", "status",
    "Opening Balance WL", "Closing Balance WL",
  ],
  Admin: [
    "id", "ID", "BC Name", "Product Type", "Product", "Amount",
    "Opening Balance Admin", "Closing Balance Admin",
    "Opening Balance BC", "Closing Balance BC",
    "Customer No", "Status", "TXN Flow", "REF No", "created_at",
  ],
  "Main Admin": null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
 const getLast7Days = () => {
    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 1)  ;
  
    return {
      start: last7.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    };
  };
  
 const getTodayRange = () => {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  return {
    start: todayStr,
    end: todayStr,
  };
};




const buildUrl = (userid, start, end) =>
  `https://pro.mokshiq.in/credit_debit_action?user=${userid}&filter_date=${start}&end_date=${end}`;

const tabStyle = (active) => ({
  padding: "6px 18px",
  borderRadius: "6px",
  border: "none",
  cursor: "pointer",
  fontWeight: active ? "600" : "400",
  background: active ? "#4f46e5" : "#e5e7eb",
  color: active ? "#fff" : "#374151",
  transition: "all 0.15s",
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const CreditDebitReportsPage = () => {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
const { start, end } = getTodayRange();


  const [apiUrl, setApiUrl]         = useState(() => buildUrl(userid, start, end));
  const [productTab, setProductTab] = useState("all"); // "all" | "credit_debit" | "fund_transfer"

  const tableData = useTableData(apiUrl, 0, creditDebitReportsConfig);

  // ── filter rows by selected product tab ───────────────────────────────────
  const filteredTableData = useMemo(() => {
    if (!tableData?.data) return tableData;
    if (productTab === "all") return tableData;
    return {
      ...tableData,
      data: tableData.data.filter((row) => row.product === productTab),
    };
  }, [tableData, productTab]);

  // ── filter columns by role + active tab ───────────────────────────────────
  const filteredConfig = useMemo(() => {
    // Main Admin always sees everything
    if (CREDIT_DEBIT_COLUMNS[userRole] === null) return creditDebitReportsConfig;

    let keys;
    if (productTab === "fund_transfer") {
      keys = FUND_TRANSFER_COLUMNS[userRole] || [];
    } else if (productTab === "credit_debit") {
      keys = CREDIT_DEBIT_COLUMNS[userRole] || [];
    } else {
      // "all" — union of both sets so no column is hidden
      const cd = CREDIT_DEBIT_COLUMNS[userRole]  || [];
      const ft = FUND_TRANSFER_COLUMNS[userRole] || [];
      keys = [...new Set([...cd, ...ft])];
    }

    if (!keys.length) return { ...creditDebitReportsConfig, columns: [] };

    const allowedSet = new Set(keys);
    return {
      ...creditDebitReportsConfig,
      columns: creditDebitReportsConfig.columns.filter((col) =>
        allowedSet.has(col.key)
      ),
    };
  }, [userRole, productTab]);

  // ── tab counts ─────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const rows = tableData?.data || [];
    return {
      all:           rows.length,
      credit_debit:  rows.filter((r) => r.product === "credit_debit").length,
      fund_transfer: rows.filter((r) => r.product === "fund_transfer").length,
    };
  }, [tableData]);

  const handleDateFilter = (s, e) => {
    setApiUrl(buildUrl(userid, s, e));
  };

  return (
    // PageWrapper gets the base config for date-filter / title logic
    <PageWrapper
      config={creditDebitReportsConfig}
      onDateFilter={handleDateFilter}
    >
      {/* ── Product type tabs ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
        <button style={tabStyle(productTab === "all")}
          onClick={() => setProductTab("all")}>
          All ({counts.all})
        </button>
        <button style={tabStyle(productTab === "credit_debit")}
          onClick={() => setProductTab("credit_debit")}>
          Credit / Debit ({counts.credit_debit})
        </button>
        <button style={tabStyle(productTab === "fund_transfer")}
          onClick={() => setProductTab("fund_transfer")}>
          Fund Transfer / Return ({counts.fund_transfer})
        </button>
      </div>

      {/* DataTable gets filtered rows + filtered columns */}
      <DataTable
        config={filteredConfig}
        tableData={filteredTableData}
      />
    </PageWrapper>
  );
};

export default CreditDebitReportsPage;